import { Fragment, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Phone, AlertCircle, UserCheck, UserPlus, PhoneMissed, TrendingUp,
  Timer, FileSpreadsheet, Zap, Clock, BookOpen, Trophy, MessageSquareText,
  PhoneCall, PhoneOff, HandCoins, AlertTriangle,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { fmtDateUk } from "@/lib/utils";
import { DateRangePicker } from "@/components/date-range-picker";
import type { DateRange } from "react-day-picker";
import { Button } from "@/components/ui/button";

function defaultRange(): DateRange {
  const today = new Date();
  // Previous two complete calendar months — the default the API uses.
  const start = new Date(today.getFullYear(), today.getMonth() - 2, 1);
  const end = new Date(today.getFullYear(), today.getMonth(), 0); // last day of prev month
  return { from: start, to: end };
}

function fmtIso(d: Date | undefined): string | undefined {
  if (!d) return undefined;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

interface CsSummary {
  sinceDate: string;
  totals: {
    total_calls: number;
    inbound: number;
    outbound: number;
    avg_duration_sec: number;
    avg_talk_sec: number;
    avg_ux_score: number;
    distinct_handlers: number;
  } | null;
  byPerson: Array<{
    handler: string;
    calls: number;
    inbound: number;
    outbound: number;
    avg_duration_sec: number;
    avg_ux_score: number;
  }>;
  daily: Array<{ day: string; inbound: number; outbound: number }>;
  dataGaps: string[];
}

type Tally = { answered: number; abandoned: number; voicemail: number; missed: number; total: number };
type CustomerState = "existing" | "new_prospect";
type Bucket = "ooh_early" | "in_hours" | "ooh_late";

interface PatienceBuckets {
  lt10: number;
  s10_30: number;
  s30_60: number;
  gte60: number;
  total: number;
  avg_sec: number;
  median_sec: number;
}

interface CallAnalysis {
  rangeStart: string;
  rangeEnd: string;
  months: Array<{ key: string; label: string }>;
  overall: Tally;
  byMonth: Record<string, Tally>;
  byBucket: Record<Bucket, Tally>;
  byCustomer: Record<CustomerState, Tally>;
  byMonthBucketCustomer: Record<string, Record<Bucket, Record<CustomerState, Tally>>>;
  newProspects: {
    unique_callers: number;
    converted_to_customer: number;
    conversion_pct: number | null;
  };
  abandonedPatience: {
    overall: PatienceBuckets;
    byMonth: Record<string, PatienceBuckets>;
    byCustomer: Record<CustomerState, PatienceBuckets>;
    newProspectsByMonth: Record<string, PatienceBuckets>;
    newProspectsLeakage: {
      unique_callers: number;
      converted_to_customer: number;
      conversion_pct: number | null;
    };
  };
  answeredRingTime?: {
    overall: PatienceBuckets;
    byCustomer: Record<CustomerState, PatienceBuckets>;
  };
  heroStats?: {
    total_inbound: number;
    total_answered: number;
    total_unanswered: number;
    unanswered_pct: number | null;
    avg_talk_time_sec: number | null;
    median_pickup_sec: number | null;
    hung_up_while_ringing: number;
    waited_10_60_then_gave_up: number;
    talk_time_sample_size: number;
  };
  segmentRates?: {
    existing: { inbound: number; abandoned: number; voicemail: number; hangup_pct: number | null; voicemail_pct: number | null };
    new_prospect: { inbound: number; abandoned: number; voicemail: number; hangup_pct: number | null; voicemail_pct: number | null };
    median_pickup_existing_sec: number | null;
    median_pickup_new_sec: number | null;
  };
  dataGaps: string[];
}

interface LeadAnalysis {
  rangeStart: string;
  rangeEnd: string;
  totals: {
    total: number;
    in_hours: number;
    ooh_total: number;
    in_hours_pct: number | null;
    ooh_pct: number | null;
  };
  byBucket: {
    in_hours: number;
    weekend: number;
    ooh_early: number;
    ooh_late: number;
  };
  timeToCall: {
    analysed: number;
    called_back: number;
    never_called: number;
    never_called_pct: number | null;
    answered_by_customer: number;
    median_seconds: number;
    median_answered_seconds: number;
    within_5_min: number;
    within_15_min: number;
    within_5_min_pct?: number | null;
    buckets: Array<{ key: string; label: string; n: number }>;
  };
  cost?: {
    cost_per_lead_gbp: number;
    ad_spend_estimate_gbp: number;
    is_placeholder: boolean;
    note: string;
  };
  dataGaps: string[];
}

function fmtInt(n: number | undefined | null): string {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString();
}
function fmtSec(n: number | undefined | null): string {
  if (n === null || n === undefined) return "—";
  const mins = Math.floor(n / 60);
  const secs = Math.round(n % 60);
  return `${mins}m ${secs.toString().padStart(2, "0")}s`;
}

export default function CustomerServiceHub() {
  const [dateRange, setDateRange] = useState<DateRange | undefined>(defaultRange());
  const from = fmtIso(dateRange?.from);
  const to = fmtIso(dateRange?.to);
  const rangeQs = from && to ? `?from=${from}&to=${to}` : "";

  const { data, isLoading, error } = useQuery<CsSummary>({
    queryKey: ["cs-summary", from, to],
    queryFn: async () => {
      const res = await fetch(`/api/dashboards/customer-service${rangeQs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  const callAnalysis = useQuery<CallAnalysis>({
    queryKey: ["cs-call-analysis", from, to],
    queryFn: async () => {
      const res = await fetch(`/api/dashboards/customer-service-calls${rangeQs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  const leadAnalysis = useQuery<LeadAnalysis>({
    queryKey: ["cs-lead-analysis", from, to],
    queryFn: async () => {
      const res = await fetch(`/api/dashboards/customer-service-leads${rangeQs}`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error(`${res.status}`);
      return res.json();
    },
  });

  if (isLoading)
    return (
      <div className="p-6 text-muted-foreground">Loading 8x8 call data from BigQuery…</div>
    );
  if (error || !data)
    return (
      <div className="p-6 text-red-600">
        Failed to load: {error instanceof Error ? error.message : "unknown error"}
      </div>
    );

  const { totals, byPerson, daily, sinceDate, dataGaps } = data;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Phone className="h-6 w-6" /> Customer Service Hub
          </h1>
          <p className="text-sm text-muted-foreground">
            8x8 Work calls · {from && to ? `${from} → ${to}` : `since ${fmtDateUk(sinceDate)}`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <DateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDateRange(defaultRange())}
          >
            Reset
          </Button>
        </div>
      </div>

      <HeroKpiBand
        callAnalysis={callAnalysis.data}
        leadAnalysis={leadAnalysis.data}
      />

      <WhySpeedMattersPanel />

      <CallAnalysisSection analysis={callAnalysis.data} isLoading={callAnalysis.isLoading} error={callAnalysis.error} />

      <ExistingVsNewComparison analysis={callAnalysis.data} />

      <SalesLeadResponseSection analysis={leadAnalysis.data} isLoading={leadAnalysis.isLoading} error={leadAnalysis.error} />

      <FooterCta />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {totals && (
          <>
            <KpiCard label="Total calls" value={fmtInt(totals.total_calls)} />
            <KpiCard label="Inbound" value={fmtInt(totals.inbound)} />
            <KpiCard label="Outbound" value={fmtInt(totals.outbound)} />
            <KpiCard label="Avg duration" value={fmtSec(totals.avg_duration_sec)} />
            <KpiCard label="Avg talk time" value={fmtSec(totals.avg_talk_sec)} />
            <KpiCard label="Avg UX score" value={String(totals.avg_ux_score ?? "—")} />
            <KpiCard label="Distinct handlers" value={fmtInt(totals.distinct_handlers)} />
          </>
        )}
      </div>

      <Card>
        <CardHeader>
          <CardTitle>By handler</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Person</th>
                <th className="text-right">Calls</th>
                <th className="text-right">Inbound</th>
                <th className="text-right">Outbound</th>
                <th className="text-right">Avg dur.</th>
                <th className="text-right">Avg UX</th>
              </tr>
            </thead>
            <tbody>
              {byPerson.map((p) => (
                <tr key={p.handler} className="border-t">
                  <td className="py-1.5 pr-2">{p.handler}</td>
                  <td className="text-right">{fmtInt(p.calls)}</td>
                  <td className="text-right">{fmtInt(p.inbound)}</td>
                  <td className="text-right">{fmtInt(p.outbound)}</td>
                  <td className="text-right">{fmtSec(p.avg_duration_sec)}</td>
                  <td className="text-right font-medium">{p.avg_ux_score ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Daily call volume</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Day</th>
                <th className="text-right">Inbound</th>
                <th className="text-right">Outbound</th>
              </tr>
            </thead>
            <tbody>
              {daily.map((d) => (
                <tr key={d.day} className="border-t">
                  <td className="py-1.5 pr-2">{d.day}</td>
                  <td className="text-right">{fmtInt(d.inbound)}</td>
                  <td className="text-right">{fmtInt(d.outbound)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {dataGaps.length > 0 && (
        <Card className="border-amber-500/40">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" /> Data gaps
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              {dataGaps.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

function KpiCard({ label, value }: { label: string; value: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-muted-foreground text-xs">{label}</div>
        <div className="text-xl font-bold">{value}</div>
      </CardContent>
    </Card>
  );
}

function KpiCardRich({
  icon,
  label,
  value,
  sub,
  tone,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
  tone?: "good" | "warn" | "bad" | undefined;
}) {
  const toneCls =
    tone === "good"
      ? "text-emerald-600"
      : tone === "warn"
      ? "text-amber-600"
      : tone === "bad"
      ? "text-red-600"
      : "";
  return (
    <Card>
      <CardContent className="p-3">
        <div className="text-muted-foreground text-xs flex items-center gap-1.5">
          {icon} {label}
        </div>
        <div className={`text-xl font-bold ${toneCls}`}>{value}</div>
        {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
      </CardContent>
    </Card>
  );
}

function pct(num: number, denom: number): string {
  if (!denom) return "—";
  return `${Math.round((num / denom) * 1000) / 10}%`;
}

function CallAnalysisSection({
  analysis,
  isLoading,
  error,
}: {
  analysis: CallAnalysis | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading)
    return (
      <Card>
        <CardContent className="p-6 text-sm text-muted-foreground">
          Loading call analysis (joining 8x8 + Commusoft)…
        </CardContent>
      </Card>
    );
  if (error || !analysis)
    return (
      <Card className="border-red-500/40">
        <CardContent className="p-6 text-sm text-red-600">
          Call analysis failed: {error instanceof Error ? error.message : "unknown error"}
        </CardContent>
      </Card>
    );

  const a = analysis;
  const ex = a.byCustomer.existing;
  const np = a.byCustomer.new_prospect;
  const exUnanswered = ex.abandoned + ex.voicemail + ex.missed;
  const npUnanswered = np.abandoned + np.voicemail + np.missed;
  const totalCalls = a.overall.total;
  const totalUnanswered = a.overall.abandoned + a.overall.voicemail + a.overall.missed;
  const inHr = a.byBucket.in_hours;
  const inHrUnanswered = inHr.abandoned + inHr.voicemail + inHr.missed;
  const ooh =
    a.byBucket.ooh_early.total + a.byBucket.ooh_late.total;
  const oohUnanswered =
    a.byBucket.ooh_early.abandoned +
    a.byBucket.ooh_early.voicemail +
    a.byBucket.ooh_early.missed +
    a.byBucket.ooh_late.abandoned +
    a.byBucket.ooh_late.voicemail +
    a.byBucket.ooh_late.missed;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">Inbound call analysis</h2>
        <p className="text-xs text-muted-foreground">
          {a.months.map((m) => m.label).join(" + ")} · existing customers vs new prospects ·
          cross-references 8x8 with Commusoft contacts (last-9-digit phone match, customer state
          determined at moment of call)
        </p>
      </div>

      {/* Top-line KPIs: customer mix + new-prospect conversion */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCardRich
          icon={<UserCheck className="h-3.5 w-3.5" />}
          label="Calls from existing customers"
          value={ex.total.toLocaleString()}
          sub={`${pct(ex.total, totalCalls)} of inbound · ${ex.answered.toLocaleString()} answered`}
          tone="good"
        />
        <KpiCardRich
          icon={<UserPlus className="h-3.5 w-3.5" />}
          label="Calls from new prospects"
          value={np.total.toLocaleString()}
          sub={`${pct(np.total, totalCalls)} of inbound · ${np.answered.toLocaleString()} answered`}
        />
        <KpiCardRich
          icon={<TrendingUp className="h-3.5 w-3.5" />}
          label="New prospect → customer"
          value={`${a.newProspects.converted_to_customer} of ${a.newProspects.unique_callers}`}
          sub={`${a.newProspects.conversion_pct ?? "—"}% of unique new callers later became Commusoft customers`}
          tone={
            a.newProspects.conversion_pct === null
              ? undefined
              : a.newProspects.conversion_pct >= 10
              ? "good"
              : a.newProspects.conversion_pct >= 5
              ? "warn"
              : "bad"
          }
        />
        <KpiCardRich
          icon={<PhoneMissed className="h-3.5 w-3.5" />}
          label="Total unanswered"
          value={totalUnanswered.toLocaleString()}
          sub={`${pct(totalUnanswered, totalCalls)} of inbound · ${a.overall.answered.toLocaleString()} answered`}
          tone="bad"
        />
      </div>

      {/* Visual outcome mix: side-by-side stacked bars + horizontal bars */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <UserCheck className="h-4 w-4 text-emerald-600" /> Existing customers — outcome mix
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {ex.total.toLocaleString()} calls — {pct(ex.answered, ex.total)} answered, {pct(exUnanswered, ex.total)} unanswered
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <StackedBar
              total={ex.total}
              segments={[
                { label: "Answered", value: ex.answered, color: "emerald" },
                { label: "Voicemail", value: ex.voicemail, color: "amber" },
                { label: "Abandoned", value: ex.abandoned, color: "red" },
                { label: "Missed", value: ex.missed, color: "gray" },
              ]}
            />
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-blue-600" /> New prospects — outcome mix
            </CardTitle>
            <p className="text-xs text-muted-foreground">
              {np.total.toLocaleString()} calls — {pct(np.answered, np.total)} answered, {pct(npUnanswered, np.total)} unanswered
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            <StackedBar
              total={np.total}
              segments={[
                { label: "Answered", value: np.answered, color: "emerald" },
                { label: "Voicemail", value: np.voicemail, color: "amber" },
                { label: "Abandoned", value: np.abandoned, color: "red" },
                { label: "Missed", value: np.missed, color: "gray" },
              ]}
            />
          </CardContent>
        </Card>
      </div>

      {/* Visual: patience profile (existing vs new) */}
      {a.abandonedPatience && (() => {
        const pe = a.abandonedPatience.byCustomer.existing;
        const pn = a.abandonedPatience.byCustomer.new_prospect;
        const max = Math.max(pe.lt10, pe.s10_30, pe.s30_60, pe.gte60, pn.lt10, pn.s10_30, pn.s30_60, pn.gte60, 1);
        return (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <PhoneMissed className="h-4 w-4 text-red-600" /> Patience profile — time on line before hanging up
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Compare how long each cohort held before abandoning. The 30–60s bucket is the
                "almost-got-them" zone — disproportionately new prospects.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <div className="text-sm font-medium mb-2 text-emerald-700">Existing customers ({pe.total})</div>
                  <div className="space-y-2">
                    <HBar label="Under 10s" value={pe.lt10} max={max} color="emerald" rightLabel={pct(pe.lt10, pe.total)} />
                    <HBar label="10–30s" value={pe.s10_30} max={max} color="emerald" rightLabel={pct(pe.s10_30, pe.total)} />
                    <HBar label="30–60s" value={pe.s30_60} max={max} color="emerald" rightLabel={pct(pe.s30_60, pe.total)} />
                    <HBar label="60s or more" value={pe.gte60} max={max} color="emerald" rightLabel={pct(pe.gte60, pe.total)} />
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium mb-2 text-blue-700">New prospects ({pn.total})</div>
                  <div className="space-y-2">
                    <HBar label="Under 10s" value={pn.lt10} max={max} color="blue" rightLabel={pct(pn.lt10, pn.total)} />
                    <HBar label="10–30s" value={pn.s10_30} max={max} color="blue" rightLabel={pct(pn.s10_30, pn.total)} />
                    <HBar label="30–60s" value={pn.s30_60} max={max} color="blue" rightLabel={pct(pn.s30_60, pn.total)} />
                    <HBar label="60s or more" value={pn.gte60} max={max} color="blue" rightLabel={pct(pn.gte60, pn.total)} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Visual: ring time before pickup (for answered calls) */}
      {a.answeredRingTime && (() => {
        const re = a.answeredRingTime.byCustomer.existing;
        const rn = a.answeredRingTime.byCustomer.new_prospect;
        const max = Math.max(re.lt10, re.s10_30, re.s30_60, re.gte60, rn.lt10, rn.s10_30, rn.s30_60, rn.gte60, 1);
        return (
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Timer className="h-4 w-4 text-sky-600" /> Ring time before pickup — answered calls
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                How long callers waited before someone picked up. Median:
                {" "}<b>{re.median_sec}s</b> existing · <b>{rn.median_sec}s</b> new prospect.
                Pickup speed is fair — the issue isn't who gets prioritised, it's overall capacity.
              </p>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
                <div>
                  <div className="text-sm font-medium mb-2 text-emerald-700">Existing customers ({re.total})</div>
                  <div className="space-y-2">
                    <HBar label="Picked up under 10s" value={re.lt10} max={max} color="emerald" rightLabel={pct(re.lt10, re.total)} />
                    <HBar label="10–30s" value={re.s10_30} max={max} color="emerald" rightLabel={pct(re.s10_30, re.total)} />
                    <HBar label="30–60s" value={re.s30_60} max={max} color="amber" rightLabel={pct(re.s30_60, re.total)} />
                    <HBar label="60s or more" value={re.gte60} max={max} color="red" rightLabel={pct(re.gte60, re.total)} />
                  </div>
                </div>
                <div>
                  <div className="text-sm font-medium mb-2 text-blue-700">New prospects ({rn.total})</div>
                  <div className="space-y-2">
                    <HBar label="Picked up under 10s" value={rn.lt10} max={max} color="emerald" rightLabel={pct(rn.lt10, rn.total)} />
                    <HBar label="10–30s" value={rn.s10_30} max={max} color="blue" rightLabel={pct(rn.s10_30, rn.total)} />
                    <HBar label="30–60s" value={rn.s30_60} max={max} color="amber" rightLabel={pct(rn.s30_60, rn.total)} />
                    <HBar label="60s or more" value={rn.gte60} max={max} color="red" rightLabel={pct(rn.gte60, rn.total)} />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}

      {/* Customer breakdown table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Existing vs new — outcome breakdown</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Caller</th>
                <th className="text-right">Total</th>
                <th className="text-right">Answered</th>
                <th className="text-right">Abandoned</th>
                <th className="text-right">Voicemail</th>
                <th className="text-right">Missed</th>
                <th className="text-right">Unanswered %</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="py-1.5 pr-2 font-medium">Existing customers</td>
                <td className="text-right">{ex.total.toLocaleString()}</td>
                <td className="text-right">{ex.answered.toLocaleString()}</td>
                <td className="text-right">{ex.abandoned.toLocaleString()}</td>
                <td className="text-right">{ex.voicemail.toLocaleString()}</td>
                <td className="text-right">{ex.missed.toLocaleString()}</td>
                <td className="text-right font-medium">{pct(exUnanswered, ex.total)}</td>
              </tr>
              <tr className="border-t">
                <td className="py-1.5 pr-2 font-medium">New prospects</td>
                <td className="text-right">{np.total.toLocaleString()}</td>
                <td className="text-right">{np.answered.toLocaleString()}</td>
                <td className="text-right">{np.abandoned.toLocaleString()}</td>
                <td className="text-right">{np.voicemail.toLocaleString()}</td>
                <td className="text-right">{np.missed.toLocaleString()}</td>
                <td className="text-right font-medium">{pct(npUnanswered, np.total)}</td>
              </tr>
              <tr className="border-t bg-muted/30 font-semibold">
                <td className="py-1.5 pr-2">All inbound</td>
                <td className="text-right">{totalCalls.toLocaleString()}</td>
                <td className="text-right">{a.overall.answered.toLocaleString()}</td>
                <td className="text-right">{a.overall.abandoned.toLocaleString()}</td>
                <td className="text-right">{a.overall.voicemail.toLocaleString()}</td>
                <td className="text-right">{a.overall.missed.toLocaleString()}</td>
                <td className="text-right">{pct(totalUnanswered, totalCalls)}</td>
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Time-of-day buckets */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">In-hours vs out-of-hours</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left text-muted-foreground">
              <tr>
                <th className="py-1">Window</th>
                <th className="text-right">Total</th>
                <th className="text-right">Answered</th>
                <th className="text-right">Abandoned</th>
                <th className="text-right">Voicemail</th>
                <th className="text-right">Missed</th>
                <th className="text-right">% of total</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-t">
                <td className="py-1.5 pr-2">09:00 – 17:00 (in hours)</td>
                <td className="text-right">{inHr.total.toLocaleString()}</td>
                <td className="text-right">{inHr.answered.toLocaleString()}</td>
                <td className="text-right">{inHr.abandoned.toLocaleString()}</td>
                <td className="text-right">{inHr.voicemail.toLocaleString()}</td>
                <td className="text-right">{inHr.missed.toLocaleString()}</td>
                <td className="text-right font-medium">{pct(inHr.total, totalCalls)}</td>
              </tr>
              <tr className="border-t">
                <td className="py-1.5 pr-2">00:00 – 09:00 (OOH early)</td>
                <td className="text-right">{a.byBucket.ooh_early.total.toLocaleString()}</td>
                <td className="text-right">{a.byBucket.ooh_early.answered.toLocaleString()}</td>
                <td className="text-right">{a.byBucket.ooh_early.abandoned.toLocaleString()}</td>
                <td className="text-right">{a.byBucket.ooh_early.voicemail.toLocaleString()}</td>
                <td className="text-right">{a.byBucket.ooh_early.missed.toLocaleString()}</td>
                <td className="text-right">{pct(a.byBucket.ooh_early.total, totalCalls)}</td>
              </tr>
              <tr className="border-t">
                <td className="py-1.5 pr-2">17:00 – 24:00 (OOH late)</td>
                <td className="text-right">{a.byBucket.ooh_late.total.toLocaleString()}</td>
                <td className="text-right">{a.byBucket.ooh_late.answered.toLocaleString()}</td>
                <td className="text-right">{a.byBucket.ooh_late.abandoned.toLocaleString()}</td>
                <td className="text-right">{a.byBucket.ooh_late.voicemail.toLocaleString()}</td>
                <td className="text-right">{a.byBucket.ooh_late.missed.toLocaleString()}</td>
                <td className="text-right">{pct(a.byBucket.ooh_late.total, totalCalls)}</td>
              </tr>
              <tr className="border-t bg-muted/30 font-semibold">
                <td className="py-1.5 pr-2">OOH total</td>
                <td className="text-right">{ooh.toLocaleString()}</td>
                <td className="text-right">
                  {(a.byBucket.ooh_early.answered + a.byBucket.ooh_late.answered).toLocaleString()}
                </td>
                <td className="text-right">
                  {(a.byBucket.ooh_early.abandoned + a.byBucket.ooh_late.abandoned).toLocaleString()}
                </td>
                <td className="text-right">
                  {(a.byBucket.ooh_early.voicemail + a.byBucket.ooh_late.voicemail).toLocaleString()}
                </td>
                <td className="text-right">
                  {(a.byBucket.ooh_early.missed + a.byBucket.ooh_late.missed).toLocaleString()}
                </td>
                <td className="text-right">{pct(ooh, totalCalls)}</td>
              </tr>
            </tbody>
          </table>
          <div className="text-xs text-muted-foreground mt-3">
            In-hours unanswered: <b>{inHrUnanswered.toLocaleString()}</b> ({pct(inHrUnanswered, inHr.total)} of in-hours calls).
            OOH unanswered: <b>{oohUnanswered.toLocaleString()}</b> ({pct(oohUnanswered, ooh)} of OOH calls).
          </div>
        </CardContent>
      </Card>

      {/* Hang-up patience distribution — how long callers waited before
          abandoning. Headline metric: each "<10s" hang-up is essentially a
          one-ring drop (IVR friction or instant regret); "60s+" hangs are
          callers who actively wanted to speak to us. */}
      {a.abandonedPatience && (
        <Card className="border-amber-500/30">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <PhoneMissed className="h-4 w-4 text-amber-600" /> Hang-ups before voicemail —
              how long they held
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Callers who hung up before reaching voicemail ({a.abandonedPatience.overall.total.toLocaleString()} total across
              {" "}{a.months.map((m) => m.label).join(" + ")}). Wait time = ring duration before drop.
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Cohort</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">&lt; 10s</th>
                  <th className="text-right">10–30s</th>
                  <th className="text-right">30–60s</th>
                  <th className="text-right">60s+</th>
                  <th className="text-right">Avg wait</th>
                  <th className="text-right">Median</th>
                </tr>
              </thead>
              <tbody>
                {a.months.map((m) => {
                  const p = a.abandonedPatience.byMonth[m.key];
                  if (!p) return null;
                  return (
                    <tr key={m.key} className="border-t">
                      <td className="py-1.5 pr-2 font-medium">{m.label}</td>
                      <td className="text-right">{p.total.toLocaleString()}</td>
                      <td className="text-right">
                        {p.lt10} <span className="text-muted-foreground text-xs">({pct(p.lt10, p.total)})</span>
                      </td>
                      <td className="text-right">
                        {p.s10_30} <span className="text-muted-foreground text-xs">({pct(p.s10_30, p.total)})</span>
                      </td>
                      <td className="text-right">
                        {p.s30_60} <span className="text-muted-foreground text-xs">({pct(p.s30_60, p.total)})</span>
                      </td>
                      <td className="text-right">
                        {p.gte60} <span className="text-muted-foreground text-xs">({pct(p.gte60, p.total)})</span>
                      </td>
                      <td className="text-right">{p.avg_sec}s</td>
                      <td className="text-right">{p.median_sec}s</td>
                    </tr>
                  );
                })}
                <tr className="border-t">
                  <td className="py-1.5 pr-2 text-emerald-700">Existing customers</td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.existing.total.toLocaleString()}</td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.existing.lt10}{" "}
                    <span className="text-muted-foreground text-xs">
                      ({pct(a.abandonedPatience.byCustomer.existing.lt10, a.abandonedPatience.byCustomer.existing.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.existing.s10_30}{" "}
                    <span className="text-muted-foreground text-xs">
                      ({pct(a.abandonedPatience.byCustomer.existing.s10_30, a.abandonedPatience.byCustomer.existing.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.existing.s30_60}{" "}
                    <span className="text-muted-foreground text-xs">
                      ({pct(a.abandonedPatience.byCustomer.existing.s30_60, a.abandonedPatience.byCustomer.existing.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.existing.gte60}{" "}
                    <span className="text-muted-foreground text-xs">
                      ({pct(a.abandonedPatience.byCustomer.existing.gte60, a.abandonedPatience.byCustomer.existing.total)})
                    </span>
                  </td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.existing.avg_sec}s</td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.existing.median_sec}s</td>
                </tr>
                <tr className="border-t">
                  <td className="py-1.5 pr-2 text-blue-700">New prospects</td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.new_prospect.total.toLocaleString()}</td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.new_prospect.lt10}{" "}
                    <span className="text-muted-foreground text-xs">
                      ({pct(a.abandonedPatience.byCustomer.new_prospect.lt10, a.abandonedPatience.byCustomer.new_prospect.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.new_prospect.s10_30}{" "}
                    <span className="text-muted-foreground text-xs">
                      ({pct(a.abandonedPatience.byCustomer.new_prospect.s10_30, a.abandonedPatience.byCustomer.new_prospect.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.new_prospect.s30_60}{" "}
                    <span className="text-muted-foreground text-xs">
                      ({pct(a.abandonedPatience.byCustomer.new_prospect.s30_60, a.abandonedPatience.byCustomer.new_prospect.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.new_prospect.gte60}{" "}
                    <span className="text-muted-foreground text-xs">
                      ({pct(a.abandonedPatience.byCustomer.new_prospect.gte60, a.abandonedPatience.byCustomer.new_prospect.total)})
                    </span>
                  </td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.new_prospect.avg_sec}s</td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.new_prospect.median_sec}s</td>
                </tr>
                <tr className="border-t bg-muted/30 font-semibold">
                  <td className="py-1.5 pr-2">All hang-ups</td>
                  <td className="text-right">{a.abandonedPatience.overall.total.toLocaleString()}</td>
                  <td className="text-right">
                    {a.abandonedPatience.overall.lt10}{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      ({pct(a.abandonedPatience.overall.lt10, a.abandonedPatience.overall.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.overall.s10_30}{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      ({pct(a.abandonedPatience.overall.s10_30, a.abandonedPatience.overall.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.overall.s30_60}{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      ({pct(a.abandonedPatience.overall.s30_60, a.abandonedPatience.overall.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.overall.gte60}{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      ({pct(a.abandonedPatience.overall.gte60, a.abandonedPatience.overall.total)})
                    </span>
                  </td>
                  <td className="text-right">{a.abandonedPatience.overall.avg_sec}s</td>
                  <td className="text-right">{a.abandonedPatience.overall.median_sec}s</td>
                </tr>
              </tbody>
            </table>
            <div className="text-xs text-muted-foreground mt-3">
              <b>Read this as:</b> &lt;10s drops are mostly IVR-friction / one-ring abandons (typically not recoverable).
              60s+ hangs are callers who actively wanted to speak to us and gave up — those are direct lost business.
            </div>
          </CardContent>
        </Card>
      )}

      {/* New-prospect "bucket leak" — same patience cuts but isolated to
          first-time callers, with the headline conversion rate. */}
      {a.abandonedPatience?.newProspectsByMonth && (
        <Card className="border-blue-500/30">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <UserPlus className="h-4 w-4 text-blue-600" /> New-prospect hang-ups — how leaky
              is the bucket?
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Hang-ups before voicemail, isolated to first-time callers (phone never seen in
              Commusoft before the call). These are prospects we never even spoke to.
            </p>
          </CardHeader>
          <CardContent className="overflow-x-auto space-y-3">
            <table className="w-full text-sm">
              <thead className="text-left text-muted-foreground">
                <tr>
                  <th className="py-1">Month</th>
                  <th className="text-right">Total</th>
                  <th className="text-right">&lt; 10s</th>
                  <th className="text-right">10–30s</th>
                  <th className="text-right">30–60s</th>
                  <th className="text-right">60s+</th>
                  <th className="text-right">Avg wait</th>
                  <th className="text-right">Median</th>
                </tr>
              </thead>
              <tbody>
                {a.months.map((m) => {
                  const p = a.abandonedPatience.newProspectsByMonth[m.key];
                  if (!p) return null;
                  return (
                    <tr key={m.key} className="border-t">
                      <td className="py-1.5 pr-2 font-medium">{m.label}</td>
                      <td className="text-right">{p.total.toLocaleString()}</td>
                      <td className="text-right">
                        {p.lt10} <span className="text-muted-foreground text-xs">({pct(p.lt10, p.total)})</span>
                      </td>
                      <td className="text-right">
                        {p.s10_30} <span className="text-muted-foreground text-xs">({pct(p.s10_30, p.total)})</span>
                      </td>
                      <td className="text-right">
                        {p.s30_60} <span className="text-muted-foreground text-xs">({pct(p.s30_60, p.total)})</span>
                      </td>
                      <td className="text-right">
                        {p.gte60} <span className="text-muted-foreground text-xs">({pct(p.gte60, p.total)})</span>
                      </td>
                      <td className="text-right">{p.avg_sec}s</td>
                      <td className="text-right">{p.median_sec}s</td>
                    </tr>
                  );
                })}
                <tr className="border-t bg-muted/30 font-semibold">
                  <td className="py-1.5 pr-2">All new-prospect hang-ups</td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.new_prospect.total.toLocaleString()}</td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.new_prospect.lt10}{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      ({pct(a.abandonedPatience.byCustomer.new_prospect.lt10, a.abandonedPatience.byCustomer.new_prospect.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.new_prospect.s10_30}{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      ({pct(a.abandonedPatience.byCustomer.new_prospect.s10_30, a.abandonedPatience.byCustomer.new_prospect.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.new_prospect.s30_60}{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      ({pct(a.abandonedPatience.byCustomer.new_prospect.s30_60, a.abandonedPatience.byCustomer.new_prospect.total)})
                    </span>
                  </td>
                  <td className="text-right">
                    {a.abandonedPatience.byCustomer.new_prospect.gte60}{" "}
                    <span className="text-muted-foreground text-xs font-normal">
                      ({pct(a.abandonedPatience.byCustomer.new_prospect.gte60, a.abandonedPatience.byCustomer.new_prospect.total)})
                    </span>
                  </td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.new_prospect.avg_sec}s</td>
                  <td className="text-right">{a.abandonedPatience.byCustomer.new_prospect.median_sec}s</td>
                </tr>
              </tbody>
            </table>
            <div className="rounded-md bg-red-50 dark:bg-red-950/30 border border-red-300/40 px-3 py-2 text-sm">
              <b className="text-red-700 dark:text-red-300">Bucket leak:</b>{" "}
              <span className="font-mono">
                {a.abandonedPatience.newProspectsLeakage.unique_callers}
              </span>{" "}
              unique new prospects hung up without leaving a message. Of those, only{" "}
              <span className="font-mono">
                {a.abandonedPatience.newProspectsLeakage.converted_to_customer}
              </span>{" "}
              (
              {a.abandonedPatience.newProspectsLeakage.conversion_pct === null
                ? "—"
                : `${a.abandonedPatience.newProspectsLeakage.conversion_pct}%`}
              ) ever became Commusoft customers afterwards. The rest are gone.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Per-month deep dive (matches handwritten layout) */}
      {a.months.map((m) => {
        const mb = a.byMonthBucketCustomer[m.key];
        if (!mb) return null;
        const mTotal = a.byMonth[m.key]?.total ?? 0;
        const mUnanswered =
          (a.byMonth[m.key]?.abandoned ?? 0) +
          (a.byMonth[m.key]?.voicemail ?? 0) +
          (a.byMonth[m.key]?.missed ?? 0);
        const sumUnanswered = (t: Tally) => t.abandoned + t.voicemail + t.missed;
        return (
          <Card key={m.key}>
            <CardHeader>
              <CardTitle className="text-base">
                {m.label} · {mTotal.toLocaleString()} inbound · {mUnanswered.toLocaleString()} unanswered
              </CardTitle>
            </CardHeader>
            <CardContent className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-muted-foreground">
                  <tr>
                    <th className="py-1">Window</th>
                    <th className="text-right">Caller</th>
                    <th className="text-right">Total</th>
                    <th className="text-right">Answered</th>
                    <th className="text-right">Unanswered</th>
                    <th className="text-right">Unanswered % of month</th>
                  </tr>
                </thead>
                <tbody>
                  {(["in_hours", "ooh_early", "ooh_late"] as Bucket[]).map((b) => (
                    <Fragment key={`${m.key}-${b}`}>
                      <tr className="border-t">
                        <td className="py-1.5 pr-2" rowSpan={2}>
                          {b === "in_hours" ? "09:00 – 17:00" : b === "ooh_early" ? "00:00 – 09:00" : "17:00 – 24:00"}
                        </td>
                        <td className="text-right text-emerald-700">Existing</td>
                        <td className="text-right">{mb[b].existing.total.toLocaleString()}</td>
                        <td className="text-right">{mb[b].existing.answered.toLocaleString()}</td>
                        <td className="text-right">{sumUnanswered(mb[b].existing).toLocaleString()}</td>
                        <td className="text-right">{pct(sumUnanswered(mb[b].existing), mTotal)}</td>
                      </tr>
                      <tr className="border-t-0">
                        <td className="text-right text-blue-700">New prospect</td>
                        <td className="text-right">{mb[b].new_prospect.total.toLocaleString()}</td>
                        <td className="text-right">{mb[b].new_prospect.answered.toLocaleString()}</td>
                        <td className="text-right">{sumUnanswered(mb[b].new_prospect).toLocaleString()}</td>
                        <td className="text-right">{pct(sumUnanswered(mb[b].new_prospect), mTotal)}</td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        );
      })}

      {a.dataGaps.length > 0 && (
        <Card className="border-amber-500/40">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" /> How this is calculated
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              {a.dataGaps.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Visual helpers ──────────────────────────────────────────────────────

const BAR_COLORS = {
  emerald: "bg-emerald-500",
  amber: "bg-amber-500",
  red: "bg-red-500",
  gray: "bg-gray-400",
  blue: "bg-blue-500",
  indigo: "bg-indigo-500",
  rose: "bg-rose-500",
  sky: "bg-sky-500",
} as const;
type BarColor = keyof typeof BAR_COLORS;

function HBar({
  label,
  value,
  max,
  color = "blue",
  rightLabel,
}: {
  label: string;
  value: number;
  max: number;
  color?: BarColor;
  rightLabel?: string;
}) {
  const widthPct = max > 0 ? Math.max(2, (value / max) * 100) : 0;
  return (
    <div className="space-y-1">
      <div className="flex justify-between text-xs">
        <span className="text-muted-foreground">{label}</span>
        <span className="font-mono">
          {value.toLocaleString()}
          {rightLabel && <span className="text-muted-foreground ml-1.5">{rightLabel}</span>}
        </span>
      </div>
      <div className="h-2 w-full rounded-full bg-muted/60 overflow-hidden">
        <div className={`h-full ${BAR_COLORS[color]} transition-all`} style={{ width: `${widthPct}%` }} />
      </div>
    </div>
  );
}

function StackedBar({
  segments,
  total,
}: {
  segments: Array<{ label: string; value: number; color: BarColor; }>;
  total: number;
}) {
  return (
    <div className="space-y-2">
      <div className="flex h-3 w-full rounded-full overflow-hidden">
        {segments.map((s, i) => {
          const w = total > 0 ? (s.value / total) * 100 : 0;
          return (
            <div
              key={i}
              className={BAR_COLORS[s.color]}
              style={{ width: `${w}%` }}
              title={`${s.label}: ${s.value.toLocaleString()} (${total > 0 ? Math.round((s.value / total) * 100) : 0}%)`}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
        {segments.map((s, i) => (
          <span key={i} className="flex items-center gap-1.5">
            <span className={`w-2.5 h-2.5 rounded-sm ${BAR_COLORS[s.color]}`} />
            <span>
              {s.label}: <span className="font-mono text-foreground">{s.value.toLocaleString()}</span>
              {total > 0 && (
                <span className="ml-1 text-muted-foreground">
                  ({Math.round((s.value / total) * 100)}%)
                </span>
              )}
            </span>
          </span>
        ))}
      </div>
    </div>
  );
}

// ── Sales Lead Response Section ─────────────────────────────────────────

function fmtDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)} min`;
  if (seconds < 86400) {
    const h = Math.floor(seconds / 3600);
    const m = Math.round((seconds % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  return `${Math.round(seconds / 86400 * 10) / 10} days`;
}

const TTC_COLORS: Record<string, BarColor> = {
  lt5: "emerald",
  m5_15: "sky",
  m15_60: "blue",
  h1_4: "amber",
  h4_24: "rose",
  d1_3: "red",
  over_3d: "red",
};

function SalesLeadResponseSection({
  analysis,
  isLoading,
  error,
}: {
  analysis: LeadAnalysis | undefined;
  isLoading: boolean;
  error: unknown;
}) {
  if (isLoading)
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4" /> Sales lead response
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-muted-foreground">
          Loading lead data (cross-region: form_fill_leads + 8x8 outbound)…
        </CardContent>
      </Card>
    );
  if (error || !analysis)
    return (
      <Card className="border-red-500/40">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-red-600">
            <FileSpreadsheet className="h-4 w-4" /> Sales lead response
          </CardTitle>
        </CardHeader>
        <CardContent className="text-sm text-red-600">
          Failed to load: {error instanceof Error ? error.message : "unknown error"}
        </CardContent>
      </Card>
    );

  const t = analysis.totals;
  const b = analysis.byBucket;
  const ttc = analysis.timeToCall;
  const calledBack = ttc.called_back;
  const ttcMax = Math.max(...ttc.buckets.map((x) => x.n), ttc.never_called, 1);

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" /> Sales lead response
        </h2>
        <p className="text-xs text-muted-foreground">
          Web form fill leads (Zapier → Google Sheet → BigQuery) for the same period as the call
          analysis above. In-hours leads are joined to 8x8 outbound calls to measure speed-to-lead.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KpiCardRich
          icon={<FileSpreadsheet className="h-3.5 w-3.5" />}
          label="Total web-form leads"
          value={t.total.toLocaleString()}
          sub={`${analysis.rangeStart} → ${analysis.rangeEnd}`}
        />
        <KpiCardRich
          icon={<Clock className="h-3.5 w-3.5" />}
          label="Came in during office hours"
          value={`${t.in_hours.toLocaleString()}`}
          sub={`${t.in_hours_pct ?? "—"}% of all leads (Mon–Fri 9–5)`}
          tone="good"
        />
        <KpiCardRich
          icon={<PhoneMissed className="h-3.5 w-3.5" />}
          label="Out-of-hours leads"
          value={t.ooh_total.toLocaleString()}
          sub={`${t.ooh_pct ?? "—"}% — need an OOH capture flow`}
          tone="warn"
        />
        <KpiCardRich
          icon={<Timer className="h-3.5 w-3.5" />}
          label="Median time to first call"
          value={fmtDuration(ttc.median_seconds)}
          sub={`Industry benchmark: under 5 min`}
          tone={ttc.median_seconds <= 300 ? "good" : ttc.median_seconds <= 900 ? "warn" : "bad"}
        />
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">When leads arrive</CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            {Math.round(((b.weekend + b.ooh_early + b.ooh_late) / Math.max(t.total, 1)) * 100)}% of
            leads come in when nobody is at the desk to call them back. The OOH-late evening rush is
            the biggest single bucket.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <StackedBar
            total={t.total}
            segments={[
              { label: "Mon–Fri 9am–5pm (in-hours)", value: b.in_hours, color: "emerald" },
              { label: "Weekday eve (5pm–midnight)", value: b.ooh_late, color: "amber" },
              { label: "Weekend (Sat/Sun)", value: b.weekend, color: "rose" },
              { label: "Weekday early (12am–9am)", value: b.ooh_early, color: "indigo" },
            ]}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 pt-2">
            <HBar label="Mon–Fri 9am–5pm" value={b.in_hours} max={t.total} color="emerald" rightLabel={`${t.total > 0 ? Math.round((b.in_hours / t.total) * 100) : 0}%`} />
            <HBar label="Weekday evening (5pm+)" value={b.ooh_late} max={t.total} color="amber" rightLabel={`${t.total > 0 ? Math.round((b.ooh_late / t.total) * 100) : 0}%`} />
            <HBar label="Weekend" value={b.weekend} max={t.total} color="rose" rightLabel={`${t.total > 0 ? Math.round((b.weekend / t.total) * 100) : 0}%`} />
            <HBar label="Weekday early morning (under 9am)" value={b.ooh_early} max={t.total} color="indigo" rightLabel={`${t.total > 0 ? Math.round((b.ooh_early / t.total) * 100) : 0}%`} />
          </div>
        </CardContent>
      </Card>

      <Card className={ttc.never_called_pct && ttc.never_called_pct > 15 ? "border-red-500/40" : "border-amber-500/30"}>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Timer className="h-4 w-4" /> Speed to lead — in-hours response time
          </CardTitle>
          <p className="text-xs text-muted-foreground mt-1">
            For the {ttc.analysed.toLocaleString()} in-hours leads, how long it took from the lead
            arriving until the first outbound call to that phone number. Matched by last 9 digits.
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KpiCardRich
              icon={<Zap className="h-3.5 w-3.5" />}
              label="Called within 5 minutes"
              value={ttc.within_5_min.toLocaleString()}
              sub={`${pct(ttc.within_5_min, ttc.analysed)} of in-hours leads`}
              tone={ttc.analysed > 0 && ttc.within_5_min / ttc.analysed >= 0.3 ? "good" : "bad"}
            />
            <KpiCardRich
              icon={<Clock className="h-3.5 w-3.5" />}
              label="Called within 15 minutes"
              value={ttc.within_15_min.toLocaleString()}
              sub={`${pct(ttc.within_15_min, ttc.analysed)} of in-hours leads`}
              tone={ttc.analysed > 0 && ttc.within_15_min / ttc.analysed >= 0.5 ? "good" : "warn"}
            />
            <KpiCardRich
              icon={<PhoneMissed className="h-3.5 w-3.5" />}
              label="Never called back"
              value={ttc.never_called.toLocaleString()}
              sub={`${ttc.never_called_pct ?? "—"}% of in-hours leads — direct lost revenue`}
              tone="bad"
            />
            <KpiCardRich
              icon={<UserCheck className="h-3.5 w-3.5" />}
              label="Got through to customer"
              value={ttc.answered_by_customer.toLocaleString()}
              sub={`Median time to actual contact: ${fmtDuration(ttc.median_answered_seconds)}`}
            />
          </div>

          <div>
            <div className="text-sm font-medium mb-2">Distribution of response times</div>
            <div className="space-y-2">
              {ttc.buckets.map((bucket) => {
                const color = (TTC_COLORS[bucket.key] ?? "blue") as BarColor;
                return (
                  <HBar
                    key={bucket.key}
                    label={bucket.label}
                    value={bucket.n}
                    max={ttcMax}
                    color={color}
                    rightLabel={`${pct(bucket.n, ttc.analysed)}`}
                  />
                );
              })}
              <HBar
                label="Never called back"
                value={ttc.never_called}
                max={ttcMax}
                color="gray"
                rightLabel={`${pct(ttc.never_called, ttc.analysed)}`}
              />
            </div>
          </div>

          <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-300/40 px-3 py-2 text-sm">
            <b className="text-amber-700 dark:text-amber-300">Speed-to-lead rule of thumb:</b>{" "}
            contact rates drop ~10× after 5 minutes. We currently hit the 5-min target on{" "}
            <span className="font-mono">{ttc.within_5_min}</span> of{" "}
            <span className="font-mono">{ttc.analysed}</span> in-hours leads
            ({pct(ttc.within_5_min, ttc.analysed)}), and{" "}
            <span className="font-mono">{ttc.never_called}</span> ({pct(ttc.never_called, ttc.analysed)}){" "}
            are never called back at all.
          </div>
        </CardContent>
      </Card>

      {analysis.dataGaps.length > 0 && (
        <Card className="border-amber-500/40">
          <CardHeader>
            <CardTitle className="text-sm flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-amber-500" /> How this is calculated
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="text-sm space-y-1 list-disc list-inside text-muted-foreground">
              {analysis.dataGaps.map((g) => (
                <li key={g}>{g}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ── Hero KPI Band (Section A) ───────────────────────────────────────────

function fmtTalkTime(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) return "—";
  const m = Math.floor(sec / 60);
  const s = Math.round(sec % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
}

function HeroKpiBand({
  callAnalysis,
  leadAnalysis,
}: {
  callAnalysis: CallAnalysis | undefined;
  leadAnalysis: LeadAnalysis | undefined;
}) {
  if (!callAnalysis || !leadAnalysis) return null;
  const h = callAnalysis.heroStats;
  if (!h) return null;
  const unansweredTone: "good" | "warn" | "bad" =
    (h.unanswered_pct ?? 0) >= 40 ? "bad" : (h.unanswered_pct ?? 0) >= 20 ? "warn" : "good";
  const ttc = leadAnalysis.timeToCall;
  const within5MinPct = ttc.within_5_min_pct ?? (ttc.analysed > 0 ? Math.round((ttc.within_5_min / ttc.analysed) * 1000) / 10 : null);
  const within5Tone: "good" | "warn" | "bad" =
    (within5MinPct ?? 0) >= 50 ? "good" : (within5MinPct ?? 0) >= 20 ? "warn" : "bad";

  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      <HeroCard
        label="Total inbound calls"
        value={h.total_inbound.toLocaleString()}
        sub={`${callAnalysis.rangeStart} → ${callAnalysis.rangeEnd}`}
        icon={<Phone className="h-3.5 w-3.5" />}
      />
      <HeroCard
        label="Average length of call"
        value={fmtTalkTime(h.avg_talk_time_sec)}
        sub={
          h.avg_talk_time_sec === null
            ? "needs CDR talk-time"
            : `talk only (excludes ring) · n=${h.talk_time_sample_size.toLocaleString()}`
        }
        icon={<MessageSquareText className="h-3.5 w-3.5" />}
      />
      <HeroCard
        label="Unanswered"
        value={`${h.unanswered_pct ?? "—"}%`}
        sub={`${h.total_unanswered.toLocaleString()} of ${h.total_inbound.toLocaleString()}`}
        tone={unansweredTone}
        icon={<PhoneOff className="h-3.5 w-3.5" />}
      />
      <HeroCard
        label="Median pickup time"
        value={h.median_pickup_sec === null ? "—" : `${h.median_pickup_sec}s`}
        sub="of answered calls"
        tone={(h.median_pickup_sec ?? 999) <= 15 ? "good" : "warn"}
        icon={<Timer className="h-3.5 w-3.5" />}
      />
      <HeroCard
        label="Total paid leads"
        value={leadAnalysis.totals.total.toLocaleString()}
        sub={`${leadAnalysis.totals.in_hours.toLocaleString()} in-hours · ${leadAnalysis.totals.ooh_total.toLocaleString()} OOH`}
        icon={<HandCoins className="h-3.5 w-3.5" />}
      />
      <HeroCard
        label="Leads called within 5 min"
        value={within5MinPct === null ? "—" : `${within5MinPct}%`}
        sub={`${ttc.within_5_min} of ${ttc.analysed} in-hours leads`}
        tone={within5Tone}
        icon={<Zap className="h-3.5 w-3.5" />}
      />
    </div>
  );
}

function HeroCard({
  label,
  value,
  sub,
  tone,
  icon,
}: {
  label: string;
  value: string;
  sub?: string;
  tone?: "good" | "warn" | "bad";
  icon?: React.ReactNode;
}) {
  const ring =
    tone === "bad"
      ? "border-red-500/40"
      : tone === "warn"
      ? "border-amber-500/40"
      : tone === "good"
      ? "border-emerald-500/40"
      : "";
  const valueCls =
    tone === "bad"
      ? "text-red-600"
      : tone === "warn"
      ? "text-amber-600"
      : tone === "good"
      ? "text-emerald-600"
      : "";
  return (
    <Card className={ring}>
      <CardContent className="p-4">
        <div className="text-muted-foreground text-xs flex items-center gap-1.5">
          {icon} {label}
        </div>
        <div className={`text-2xl font-bold mt-1 ${valueCls}`}>{value}</div>
        {sub && <div className="text-[11px] text-muted-foreground mt-1 leading-tight">{sub}</div>}
      </CardContent>
    </Card>
  );
}

// ── Section B: Why Speed Matters (industry evidence, static) ────────────

const ODDS_DATA = [
  { label: "Within 5 min", odds: 100, color: "bg-sky-500" },
  { label: "5–10 min", odds: 20, color: "bg-sky-400" },
  { label: "10–30 min", odds: 5, color: "bg-amber-500" },
  { label: "Over 30 min", odds: 1, color: "bg-rose-500" },
];

function WhySpeedMattersPanel() {
  return (
    <Card className="border-2 border-sky-500/30 bg-sky-50/30 dark:bg-sky-950/10">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between flex-wrap gap-2">
          <div>
            <CardTitle className="text-base flex items-center gap-2 text-sky-700 dark:text-sky-300">
              <BookOpen className="h-5 w-5" /> Why speed matters
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">
              Industry evidence — not Aquila data. How the relative odds of reaching a sales lead collapse with each passing minute.
            </p>
          </div>
          <div className="text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-md bg-sky-100 dark:bg-sky-900 text-sky-700 dark:text-sky-300 border border-sky-300/40">
            External reference
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <div className="text-sm font-medium mb-2">Relative odds of reaching a lead (5-minute call = 100)</div>
          <div className="space-y-2">
            {ODDS_DATA.map((d) => (
              <div key={d.label} className="space-y-0.5">
                <div className="flex justify-between text-xs">
                  <span className="font-medium">{d.label}</span>
                  <span className="font-mono">{d.odds}</span>
                </div>
                <div className="h-3 w-full rounded-full bg-muted/50 overflow-hidden">
                  <div className={`h-full ${d.color}`} style={{ width: `${d.odds}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <StatChip
            big="100×"
            label="more likely to reach a lead at 5 min vs 30 min"
            color="text-sky-600 dark:text-sky-300"
          />
          <StatChip
            big="21×"
            label="more likely to qualify into a booked quote"
            color="text-sky-600 dark:text-sky-300"
          />
          <StatChip
            big="78%"
            label="buy from the first firm to respond"
            color="text-emerald-600 dark:text-emerald-300"
          />
        </div>
        <div className="text-[11px] text-muted-foreground border-t pt-2">
          Source: MIT / InsideSales.com Lead Response Management Study; Harvard Business Review analysis of 2.24m leads.
        </div>
      </CardContent>
    </Card>
  );
}

function StatChip({ big, label, color }: { big: string; label: string; color?: string }) {
  return (
    <div className="rounded-lg border bg-card px-3 py-3">
      <div className={`text-3xl font-bold ${color ?? ""}`}>{big}</div>
      <div className="text-xs text-muted-foreground mt-1 leading-tight">{label}</div>
    </div>
  );
}

// ── Section D: Existing vs New comparison cards (paired) ───────────────

function ExistingVsNewComparison({ analysis }: { analysis: CallAnalysis | undefined }) {
  if (!analysis?.segmentRates) return null;
  const s = analysis.segmentRates;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <SegmentCompareCard
        title="Existing customers"
        icon={<UserCheck className="h-5 w-5 text-emerald-600" />}
        accent="emerald"
        inbound={s.existing.inbound}
        hangup={s.existing.abandoned}
        hangupPct={s.existing.hangup_pct}
        voicemail={s.existing.voicemail}
        voicemailPct={s.existing.voicemail_pct}
        medianPickup={s.median_pickup_existing_sec}
        narrative="Known to us. They'll wait, and if they can't get through, they tend to leave a voicemail."
      />
      <SegmentCompareCard
        title="New prospects"
        icon={<UserPlus className="h-5 w-5 text-blue-600" />}
        accent="blue"
        inbound={s.new_prospect.inbound}
        hangup={s.new_prospect.abandoned}
        hangupPct={s.new_prospect.hangup_pct}
        voicemail={s.new_prospect.voicemail}
        voicemailPct={s.new_prospect.voicemail_pct}
        medianPickup={s.median_pickup_new_sec}
        narrative="First contact. They almost never leave voicemail — if we don't pick up live, they're gone."
      />
      <Card className="md:col-span-2 border-muted">
        <CardContent className="p-3 text-xs text-muted-foreground">
          When we do answer, median pickup speed is essentially the same for both segments (existing {s.median_pickup_existing_sec ?? "—"}s, new {s.median_pickup_new_sec ?? "—"}s). The difference is willingness to wait, not favouritism.
        </CardContent>
      </Card>
    </div>
  );
}

function SegmentCompareCard({
  title,
  icon,
  accent,
  inbound,
  hangup,
  hangupPct,
  voicemail,
  voicemailPct,
  medianPickup,
  narrative,
}: {
  title: string;
  icon: React.ReactNode;
  accent: "emerald" | "blue";
  inbound: number;
  hangup: number;
  hangupPct: number | null;
  voicemail: number;
  voicemailPct: number | null;
  medianPickup: number | null;
  narrative: string;
}) {
  const border = accent === "emerald" ? "border-emerald-500/30" : "border-blue-500/30";
  return (
    <Card className={border}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">{icon}{title}</CardTitle>
        <p className="text-xs text-muted-foreground">{inbound.toLocaleString()} inbound calls</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <StatBlock
            big={hangupPct === null ? "—" : `${hangupPct}%`}
            label="hang up unanswered"
            sub={`${hangup.toLocaleString()} calls`}
            tone="bad"
          />
          <StatBlock
            big={voicemailPct === null ? "—" : `${voicemailPct}%`}
            label="leave voicemail"
            sub={`${voicemail.toLocaleString()} calls`}
            tone="good"
          />
        </div>
        <div className="text-xs text-muted-foreground border-t pt-2">
          Median pickup when answered: <span className="font-mono">{medianPickup === null ? "—" : `${medianPickup}s`}</span>
        </div>
        <div className="text-xs italic text-muted-foreground">{narrative}</div>
      </CardContent>
    </Card>
  );
}

function StatBlock({ big, label, sub, tone }: {
  big: string;
  label: string;
  sub?: string;
  tone?: "good" | "warn" | "bad";
}) {
  const cls =
    tone === "bad" ? "text-red-600" :
    tone === "warn" ? "text-amber-600" :
    tone === "good" ? "text-emerald-600" : "";
  return (
    <div>
      <div className={`text-2xl font-bold ${cls}`}>{big}</div>
      <div className="text-xs text-muted-foreground leading-tight">{label}</div>
      {sub && <div className="text-[11px] text-muted-foreground/80 mt-0.5">{sub}</div>}
    </div>
  );
}

// ── Section F: Footer CTA strip ────────────────────────────────────────

function FooterCta() {
  return (
    <Card className="border-2 border-sky-500/40 bg-gradient-to-r from-sky-50 to-emerald-50 dark:from-sky-950/30 dark:to-emerald-950/30">
      <CardContent className="p-5">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-center">
          <CtaItem icon={<PhoneCall className="h-5 w-5" />} title="Answer in 3 rings" />
          <CtaItem icon={<Timer className="h-5 w-5" />} title="Call leads in 3 minutes" />
          <CtaItem icon={<Trophy className="h-5 w-5" />} title="Book the appointment" />
        </div>
      </CardContent>
    </Card>
  );
}

function CtaItem({ icon, title }: { icon: React.ReactNode; title: string }) {
  return (
    <div className="flex items-center justify-center gap-2 text-base font-semibold text-sky-700 dark:text-sky-300">
      {icon}
      <span>{title}</span>
    </div>
  );
}
